import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  ObservableMap,
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
  toJS,
} from "mobx";
import { Uuid } from "@/domains/global/identifiers";
import { BaseError } from "@/domains/errors";
import { objectModule } from "@/modules/object";
import { ApiSchema, api } from "@/modules/api";
import {
  PlatformAccountState,
  PlatformStripeSubscriptionStatus,
} from "@/store/platform-info/types";
import { PlatformStripeSubscriptionInfoObservable } from "@/store/platform-info/PlatformStripeSubscriptionInfoObservable";
import { PlatformOnboardingInfoObservable } from "@/store/platform-info/PlatformOnboardingInfoObservable";
import pRetry from "p-retry";
import {
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
  buildAsyncData,
} from "@/domains/async/AsyncData";
import { logger } from "@/modules/logger";
import {
  GatedFeature,
  GatedFeatureConfig,
  GatedFeatureConfigMap,
  GatedFeatureConfigMapData,
} from "@/store/feature-gates/types";
import { CacheKey } from "@/modules/cache/constants";
import {
  buildGatedFeatureConfigMapFromData,
  buildSerializableGatedFeatureConfigMap,
} from "@/store/feature-gates/utils";
import { PerDeviceOnboardingInfoObservable } from "@/store/platform-info/PerDeviceOnboardingInfoObservable";

export class AppStorePlatformInfoStore extends AppSubStore {
  fetchPlatformInfoState = buildAsyncData<boolean>({});

  accountState: PlatformAccountState;
  perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
  onboardingInfo: PlatformOnboardingInfoObservable;
  stripeSubscriptionMap: ObservableMap<Uuid, PlatformStripeSubscriptionInfoObservable>;
  featureGateConfigs: ObservableMap<GatedFeature, GatedFeatureConfig>;

  subscribeNowInterstitialIsOpen: boolean;
  subscribeNowInterstitialOpenedManually: boolean;
  subscriptionWasCompletedSuccessfully: boolean;
  welcomeToProModalIsOpen: boolean;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.accountState = PlatformAccountState.Initial;

    this.stripeSubscriptionMap = new ObservableMap();

    this.perDeviceOnboardingInfo = new PerDeviceOnboardingInfoObservable({ store: this.store });

    /**
     * Note that we pass in `perDeviceOnboardingInfo` as an arg, because we want
     * to manage the lifecycle independently of the `PlatformOnboardingInfoObservable`.
     */
    this.onboardingInfo = new PlatformOnboardingInfoObservable({
      shouldShowIcpRfi: false,
      shouldShowHeadsUpCoachMark: false,
      rfiCompletedSteps: [],
      store: this.store,
      perDeviceOnboardingInfo: this.perDeviceOnboardingInfo,
    });

    this.subscribeNowInterstitialIsOpen = false;
    this.subscribeNowInterstitialOpenedManually = false;
    this.subscriptionWasCompletedSuccessfully = false;
    this.welcomeToProModalIsOpen = false;
    this.featureGateConfigs = new ObservableMap();

    makeObservable(this, {
      dismissIcpRfi: true,
      dismissHeadsUpCoachMark: true,
      fetchPlatformInfoState: observable,
      accountState: observable,
      perDeviceOnboardingInfo: observable,
      onboardingInfo: observable,
      stripeSubscriptionMap: observable,
      featureGateConfigs: observable,
      subscribeNowInterstitialIsOpen: observable,
      subscribeNowInterstitialOpenedManually: observable,
      subscriptionWasCompletedSuccessfully: observable,
      welcomeToProModalIsOpen: observable,
      shouldShowStripeSubscriptionInterstitial: computed,

      setSubscriptionCompletedSuccessfully: action,
      openSubscribeNowInterstitial: action,
      dismissStripeSubscriptionInterstitial: action,
      openWelcomeToProModal: action,
      closeWelcomeToProModal: action,
      fetchPlatformInfo: action,
      updateOnboardingInfoFromRemoteData: action,
      updateFeatureGateConfigsFromRemoteData: action,
      hydrateFeatureGateConfigsFromCache: action,
      initialize: action,
      getFeatureGateConfig: false,
    });
  }

  get shouldShowStripeSubscriptionInterstitial() {
    return this.subscribeNowInterstitialIsOpen;
  }

  setSubscriptionCompletedSuccessfully = () => {
    this.subscriptionWasCompletedSuccessfully = true;
  };

  openWelcomeToProModal = () => {
    this.welcomeToProModalIsOpen = true;
  };

  closeWelcomeToProModal = () => {
    this.welcomeToProModalIsOpen = false;
  };

  openSubscribeNowInterstitial = () => {
    this.subscribeNowInterstitialIsOpen = true;
    this.subscribeNowInterstitialOpenedManually = true;
  };

  dismissStripeSubscriptionInterstitial = () => {
    this.subscribeNowInterstitialIsOpen = false;
    this.subscribeNowInterstitialOpenedManually = false;
  };

  async fetchPlatformInfo() {
    runInAction(() => {
      this.fetchPlatformInfoState = asyncDataLoading({});
    });

    try {
      const response = await api.get("/v2/me/platform-info", {});

      if (response.data) {
        const responseData = response.data;

        runInAction(() => {
          this.accountState = responseData.account_state as PlatformAccountState;

          // TODO: For right now, we're disabling the other onboarding stuff besides the import CTA
          responseData.onboarding_info.onboarding_state = "ONBOARDING_COMPLETED";
          responseData.onboarding_info.onboarding_profile = {};
          this.updateOnboardingInfoFromRemoteData({ data: responseData.onboarding_info });

          const subscriptions = responseData.stripe_subscriptions;
          this.stripeSubscriptionMap.clear();

          for (const subscriptionInfo of subscriptions) {
            const subscriptionObservable = new PlatformStripeSubscriptionInfoObservable({
              id: subscriptionInfo.id,
              stripeSubscriptionId: subscriptionInfo.stripe_subscription_id,
              stripeSubscriptionStatus:
                subscriptionInfo.stripe_subscription_status as PlatformStripeSubscriptionStatus,
            });

            this.stripeSubscriptionMap.set(subscriptionObservable.id, subscriptionObservable);
          }

          // Feature gate configs for monetization
          this.updateFeatureGateConfigsFromRemoteData({
            data: (responseData.feature_gate_configs as GatedFeatureConfigMapData) ?? {},
          });
        });

        runInAction(() => {
          this.fetchPlatformInfoState = asyncDataLoaded(this.fetchPlatformInfoState, true);
        });
      } else {
        logger.error({
          message: "Failed to fetch platform info for current account.",
          info: { result: objectModule.safeAsJson(response) },
        });

        runInAction(() => {
          this.fetchPlatformInfoState = asyncDataFailed({});
        });
      }
    } catch (error) {
      logger.error({
        message: "Failed to fetch platform info for the current account.",
        info: {
          error: objectModule.safeErrorAsJson(error as Error),
        },
      });

      runInAction(() => {
        this.fetchPlatformInfoState = asyncDataFailed({});
      });
    }

    // Persist feature gate configs (data-only) for offline use
    try {
      const plainObj = Object.fromEntries(toJS(this.featureGateConfigs)) as GatedFeatureConfigMap;
      const payload = buildSerializableGatedFeatureConfigMap(plainObj);
      await this.store.accountCache.set(CacheKey.FeatureGateConfigs, payload);
    } catch (unknownErr) {
      logger.warn({
        message: "[PlatformInfo.fetchPlatformInfo] Failed to persist feature gate configs",
        info: { err: objectModule.safeErrorAsJson(unknownErr as BaseError) },
      });
    }
  }

  async dismissIcpRfi({ completedSteps }: { completedSteps: string[] }) {
    // Optimistically dismiss the ICP RFI immediately
    runInAction(() => {
      this.onboardingInfo.shouldShowIcpRfi = false;
      if (completedSteps) {
        completedSteps.forEach(step => this.onboardingInfo.markStepCompleted(step));
      }
    });

    await pRetry(async () => {
      const response = await api.post("/v2/me/platform-info/dismiss-icp-rfi");

      if (response?.data?.onboarding_info) {
        // The API endpoint is returning `[key: string]: unknown`...
        // But it should match the `PlatformInfoOnboarding` response type.
        const apiOnboardingInfo = response.data
          .onboarding_info as ApiSchema["PlatformInfoOnboarding"];

        this.updateOnboardingInfoFromRemoteData({ data: apiOnboardingInfo });
        return;
      }

      if (response?.error) {
        const err = new BaseError({
          message: "Failed to dismiss ICP RFI for current account.",
          info: {
            response: objectModule.safeAsJson(response),
          },
        });
        throw err;
      }
    });
  }

  async dismissHeadsUpCoachMark() {
    runInAction(() => {
      this.onboardingInfo.shouldShowHeadsUpCoachMark = false;
    });

    await pRetry(async () => {
      const response = await api.post("/v2/me/platform-info/dismiss-heads-up-coach-mark", {});

      if (response?.data?.onboarding_info) {
        // The API endpoint is returning `[key: string]: unknown`...
        // But it should match the `PlatformInfoOnboarding` response type.
        const apiOnboardingInfo = response.data
          .onboarding_info as ApiSchema["PlatformInfoOnboarding"];

        this.updateOnboardingInfoFromRemoteData({ data: apiOnboardingInfo });
        return;
      }

      if (response?.error) {
        const err = new BaseError({
          message: "Failed to dismiss Heads Up Coach Mark for current account.",
          info: {
            response: objectModule.safeAsJson(response),
          },
        });
        throw err;
      }
    });
  }

  updateOnboardingInfoFromRemoteData({ data }: { data: ApiSchema["PlatformInfoOnboarding"] }) {
    runInAction(() => {
      const updatedOnboardingInfo = PlatformOnboardingInfoObservable.createFromRemoteData({
        data,
        store: this.store,
        perDeviceOnboardingInfo: this.perDeviceOnboardingInfo,
      });
      this.onboardingInfo.shouldShowIcpRfi = updatedOnboardingInfo.shouldShowIcpRfi;
      this.onboardingInfo.shouldShowHeadsUpCoachMark =
        updatedOnboardingInfo.shouldShowHeadsUpCoachMark;
      this.onboardingInfo.rfiCompletedSteps = updatedOnboardingInfo.rfiCompletedSteps;
    });
  }

  updateFeatureGateConfigsFromRemoteData({ data }: { data: GatedFeatureConfigMapData }) {
    const fullConfig = buildGatedFeatureConfigMapFromData(data ?? {});
    this.featureGateConfigs.clear();
    for (const [feature, config] of Object.entries(fullConfig)) {
      this.featureGateConfigs.set(feature as GatedFeature, config);
    }
  }

  getFeatureGateConfig(gateId: GatedFeature): GatedFeatureConfig | undefined {
    return this.featureGateConfigs.get(gateId);
  }

  async hydrateFeatureGateConfigsFromCache(): Promise<boolean> {
    try {
      const cachedConfigs = await this.store.accountCache.get(CacheKey.FeatureGateConfigs);
      if (!cachedConfigs) {
        return false;
      }

      runInAction(() => {
        this.updateFeatureGateConfigsFromRemoteData({
          data: cachedConfigs as GatedFeatureConfigMapData,
        });
      });

      return true;
    } catch (unknownErr) {
      logger.warn({
        message: "[PlatformInfo.hydrateFeatureGateConfigsFromCache] Failed to hydrate from cache",
        info: { err: objectModule.safeErrorAsJson(unknownErr as BaseError) },
      });
      return false;
    }
  }

  async initialize() {
    // This hydrates any device-related data from our local cache
    await this.perDeviceOnboardingInfo.initialize();

    // Kick off network fetch
    const fetchPromise = this.fetchPlatformInfo();

    // Try to hydrate from cache for offline/fast startup
    const hydrationSuccess = await this.hydrateFeatureGateConfigsFromCache();
    if (hydrationSuccess) {
      logger.info({
        message: "[PlatformInfo.initialize] Successfully hydrated feature gate configs from cache",
      });
    }

    await fetchPromise;
  }
}
